import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';
import { IntlShape, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Link } from 'react-router-dom';
import { Flag, Icon } from 'semantic-ui-react';

import { ENDPOINTS_COMPETITIONS, API_URL } from '../../config';
import { PROFILE_PUBLIC_PAGE_PATH } from '../../shared/constants';
import { addModifiers, normalizeCapitalization } from '../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import './Competitions.scss';
import { formatNumber, language, twoDecimalCaseOption } from '../../shared/helpers/formatNumber';

const getLevelIcon = (levelObject: any, levelOptions: any, value: any) => {
  const levelLabel = levelOptions && levelOptions.get(value.level);
  levelObject.level = (
    <img
      className="card-list__item-icon"
      alt={levelLabel}
      src={`${ENDPOINTS_COMPETITIONS.competitionsLevels}/${levelObject.level.toLowerCase().replace('_', '-')}/icon`}
      title={levelLabel}
    />
  );
};

export const convertFromPermillion = (value: number) => (value * 1000000).toLocaleString(language, twoDecimalCaseOption);

export const convertFromPermillionNumber = (value: number) => value * 1000000;

/*
The Options of the endpoint /api/competitions/challenge/boards/leader doesn't
provide the fields that have the type of Country, allowing us to know if a Flag should be display.
This const define which fields should show the Flag
*/
const AttributeTypeFLag = Immutable.Set.of('country', 'universityCountry');
// TODO(hacnguyen) Remove currentLevel after backend for BRAINTECH-4213 is deployed
const tierAttributes = new Set(['currentLevel', 'geniusLevel', 'bestLevel']);

interface TeamMemberName {
  firstName: string;
  lastName: string;
  fullName: string;
}

interface Image {
  url: string;
}

interface TeamMemberObject {
  id: string;
  name: TeamMemberName;
  image?: Image;
}

export type TeamMember = string | TeamMemberObject;

export function formatUserTableCell(user: TeamMember, isMe = false) {
  return (
    <div className="competitions_data_container--string">
      <UserLink user={user} isMe={isMe} />
    </div>
  );
}

export function formatUserTableCellWithExtras(user: TeamMember, extras: string[], filterOptions?: any, isMe = false) {
  return (
    <div className="competitions_data_container--string">
      <UserLinkWithExtras user={user} extras={extras} filterOptions={filterOptions} isMe={isMe} />
    </div>
  );
}

interface UserLinkProps {
  user: TeamMember;
  isMe?: boolean;
}
export function UserLink({ user, isMe = false }: UserLinkProps) {
  const { userId, initials, image, name } = getUserFields(user);
  if (userId === null) {
    //user is pending
    return <></>;
  }

  return (
    <Link to={`${PROFILE_PUBLIC_PAGE_PATH}/${userId}`} className="competitions__user">
      <div className="competitions_data_container--user">
        <UserAvatar initials={initials} image={image} /> {name}
        {isMe && <FormattedMessageHelper id={'competitions.me'} />}
      </div>
    </Link>
  );
}

interface UserLinkWithExtrasProps {
  user: TeamMember;
  extras: string[];
  filterOptions?: any;
  isMe?: boolean;
}
export function UserLinkWithExtras({ user, extras, filterOptions, isMe = false }: UserLinkWithExtrasProps) {
  const { userId, initials, image, name } = getUserFields(user);
  if (userId === null) {
    //user is pending
    return <></>;
  }

  const iconUrls: string[] = [];

  if (Array.isArray(extras) && extras.length > 0 && filterOptions) {
    const extrasChoices = filterOptions.getIn(['extras', 'choices']);

    if (extrasChoices) {
      // Process each item in the extras array
      extras.forEach((extraItem: string) => {
        const choice = extrasChoices.find((choice: any) => choice.get('label') === extraItem);
        if (choice) {
          const relativeUrl = choice.get('value');
          const iconUrl = `${API_URL}${relativeUrl}`;
          iconUrls.push(iconUrl);
        }
      });
    }
  }

  return (
    <Link to={`${PROFILE_PUBLIC_PAGE_PATH}/${userId}`} className="competitions__user">
      <div className="competitions_data_container--user">
        <UserAvatar initials={initials} image={image} /> {name}
        {isMe && <FormattedMessageHelper id={'competitions.me'} />}
        {iconUrls.map((iconUrl, index) => {
          // Find the corresponding extra item for this iconUrl
          const extraItem = extras[index];
          return (
            <img
              key={index}
              src={iconUrl}
              alt={extraItem}
              title={extraItem}
              className="genius-extras-icon"
              style={{ marginLeft: '8px', width: '16px', height: '16px' }}
            />
          );
        })}
      </div>
    </Link>
  );
}

function getUserFields(user: string | TeamMemberObject) {
  const isString = typeof user === 'string';
  const userId = isString ? user : user.id;
  const initials = userId?.substring(0, 2) ?? '';
  const image = !isString ? user.image?.url : undefined;
  const name = !isString ? user.name?.fullName ?? userId : userId;
  return { isString, userId, initials, image, name };
}

function formatMember(user: TeamMember) {
  const { userId } = getUserFields(user);

  if (userId === null) {
    return <></>;
  }

  return <UserLink key={userId} user={user} />;
}

export function formatMembers(members: string[]) {
  if (!members) {
    return <></>;
  }
  return <div className="competitions__member-list">{members.map(member => formatMember(member))}</div>;
}

const IQC2025_INDIVIDUAL_QUALIFYING_SCORE = 10_000;

const OC2025_OSMOSIS_POINTS_QUALIFIED = 100_000;

export function formatQualifierScores(scores: number[]) {
  if (!scores) {
    return <></>;
  }

  return (
    <div className="competitions__qualifier-scores">
      {scores.map((score, idx) => {
        const isScoreQualified = score >= IQC2025_INDIVIDUAL_QUALIFYING_SCORE;
        return (
          <div key={idx} className={addModifiers('competitions__qualifier-scores-item', isScoreQualified ? ['qualified'] : [])}>
            {isScoreQualified && <Icon name="check circle" color="green" />}
            <span>{formatNumber(score, 'integer')}</span>
          </div>
        );
      })}
    </div>
  );
}

export function formatOsmosisPointsAllocated(points: number) {
  if (typeof points !== 'number') {
    return <div className="competitions_data_container--number">{points}</div>;
  }

  const isQualified = points === OC2025_OSMOSIS_POINTS_QUALIFIED;
  return (
    <div className="competitions_data_container--number">
      <div className={addModifiers('competitions__qualifier-scores-item', isQualified ? ['qualified'] : [])}>
        {isQualified && <Icon name="check circle" color="green" />}
        <span>{formatNumber(points, 'integer')}</span>
      </div>
    </div>
  );
}

interface ChangeDataToReactTableParams {
  dataTable: any;
  countryOptions: any;
  universityCountryOptions: Immutable.Map<string, string>;
  levelOptions: any;
  filterOptions: any;
  tableType: string;
  shouldDisplayLocalRank?: boolean;
}

function formatPrizeValue(value: number | undefined, intl: IntlShape) {
  if (typeof value === 'number') {
    return <div className="competitions_data_container--number">${value}</div>;
  } else if (value == null) {
    //interim results for the current week:
    return (
      <div className="competitions_data_container--number competitions_data_container--interim">
        {formatMessageHelper(intl, 'competitions.table.interim')}
      </div>
    );
  }

  return value;
}

function getFormattedNumberValue(attribute: any, valueFromObject: number, filterOptions: any) {
  if (attribute === 'actualWeight' || attribute === 'simulationWeight') {
    valueFromObject = valueFromObject * 1000000;
  }

  const rounding = filterOptions?.getIn([attribute, 'rounding']);
  const options = { ...(rounding && { numFractionDigits: rounding }) };

  return (
    <>
      <div className={addModifiers('competitions_data_container', ['number'])}>
        {formatNumber(valueFromObject, filterOptions?.getIn([attribute, 'type']), options)}
      </div>
    </>
  );
}

export interface FormatNumbersParams {
  newObject: any;
  attribute: any;
  filterOptions: any;
}

export function formatNumbers({ newObject, attribute, filterOptions }: FormatNumbersParams) {
  const numberArray: any = ['float', 'integer', 'decimal', 'number', 'decimal', 'percent', 'permyriad'];
  const valueFromObject = newObject[attribute];
  if (typeof valueFromObject === 'number' || (filterOptions && numberArray.includes(filterOptions.getIn([attribute, 'type'])))) {
    return getFormattedNumberValue(attribute, valueFromObject, filterOptions);
  }

  return <div className={addModifiers('competitions_data_container', ['string'])}>{newObject[attribute]}</div>;
}

const referralTableIntegerFields = new Set(['consultantReferrals', 'completedReferrals', 'registeredReferrals', 'rank']);

export const changeDataToReactTable = ({
  dataTable,
  countryOptions,
  universityCountryOptions,
  levelOptions,
  filterOptions,
  tableType,
  intl,
  shouldDisplayLocalRank,
}: ChangeDataToReactTableParams & InjectedIntlProps) =>
  dataTable &&
  dataTable.map((value: any) => {
    const newObject = Object.assign({}, value);

    Object.keys(newObject).map((attribute: any) => {
      if (newObject[attribute] === null && !(attribute === 'value' && tableType === 'prize')) {
        //interim results have empty value
        return;
      }

      if (attribute === 'user') {
        if (tableType === 'genius' && value.extras) {
          newObject.user = formatUserTableCellWithExtras(newObject.user, value.extras, filterOptions);
        } else {
          newObject.user = formatUserTableCell(newObject.user);
        }
      } else if (attribute === 'members') {
        newObject.members = formatMembers(newObject.members);
      } else if (attribute === 'qualifierScore') {
        newObject.qualifierScore = formatQualifierScores(newObject.qualifierScore);
      } else if (attribute.startsWith('osmosisPointsAllocated')) {
        newObject[attribute] = formatOsmosisPointsAllocated(newObject[attribute]);
      } else if (attribute === 'value' && tableType === 'prize') {
        newObject.value = formatPrizeValue(newObject.value, intl);
      } else if (attribute === 'startDate' && tableType === 'prize' && !newObject.value) {
        newObject.startDate = (
          <div className="competitions_data_container competitions_data_container--string competitions_data_container--interim">
            {newObject.startDate} {formatMessageHelper(intl, 'competitions.table.interim')}
          </div>
        );
      } else if (AttributeTypeFLag.includes(attribute) && value[attribute]) {
        const countryLabel =
          attribute === 'universityCountry'
            ? universityCountryOptions && universityCountryOptions.get(value[attribute])
            : countryOptions && countryOptions.get(value[attribute]);
        newObject[attribute] = (
          <div className={addModifiers('competitions_data_container', ['flag'])}>
            <Flag title={countryLabel} name={value[attribute].toLowerCase()} />
            <span>{countryLabel}</span>
          </div>
        );
        newObject.countryName = value.country;
      } else if (attribute === 'level') {
        getLevelIcon(newObject, levelOptions, value);
      } else if (attribute === 'rank') {
        const formattedRank = (() => {
          const rank = formatNumber(newObject.rank, filterOptions?.getIn([attribute, 'type']));

          if (shouldDisplayLocalRank && value.localRank) {
            const localRank = formatNumber(value.localRank, filterOptions?.getIn([attribute, 'type']));
            return (
              <>
                <span className="competitions__rank--secondary">({rank})</span>
                <span>{localRank}</span>
              </>
            );
          }

          return rank;
        })();

        newObject.rank = (
          <div className="competitions__rank">
            <div className="competitions_data_container--number">
              <span>{formattedRank}</span>
            </div>
          </div>
        );
      } else if (attribute === 'usersLeaderboardLink') {
        newObject.usersLeaderboardLink = <div className="competitions_data_container--modal"> {newObject.usersLeaderboardLink} </div>;
      } else if (tableType === 'referrals' && referralTableIntegerFields.has(attribute)) {
        newObject[attribute] = <div className="competitions_data_container--number">{formatNumber(newObject[attribute], 'integer')}</div>;
      } else if (tierAttributes.has(attribute) && value[attribute]) {
        newObject[attribute] = normalizeCapitalization(value[attribute]);
      } else {
        newObject[attribute] = formatNumbers({ newObject: newObject, attribute: attribute, filterOptions: filterOptions });
      }
    });

    return newObject;
  });

export const formatCompetitionValue = (column: string, field: any, options: any) => {
  if (column.startsWith('osmosisPointsAllocated')) {
    return formatOsmosisPointsAllocated(field);
  }
  const numberArray: any = ['float', 'integer', 'decimal', 'number', 'decimal', 'percent', 'permyriad'];
  let valueFromObject = field;
  if (typeof valueFromObject === 'number' || (options && numberArray.includes(options.getIn([column, 'type'])))) {
    if (column === 'actualWeight') {
      valueFromObject = valueFromObject * 1000000;
    }

    return (
      <div className={addModifiers('competitions_data_container', ['number'])}>
        {formatNumber(valueFromObject, options && options.getIn([column, 'type']))}
      </div>
    );
  } else {
    if (tierAttributes.has(column)) {
      field = normalizeCapitalization(field);
    }

    return <div className={addModifiers('competitions_data_container', ['string'])}>{field}</div>;
  }
};

export const countRangePerfomePeriodDaily = (
  startDate: number,
  endDate: number,
  records: Immutable.List<Immutable.List<number>>
): number => {
  let total: number = 0;
  records.forEach((record: Immutable.List<number>) => {
    if (moment(record.first()).isSameOrAfter(moment(startDate), 'd') && moment(record.first()).isSameOrBefore(moment(endDate), 'd')) {
      const lastRecord: number = record.last();
      total = total + lastRecord;
    }
  });

  return total;
};

export const countRangePerfomePeriodCumulative = (
  startDate: number,
  endDate: number,
  records: Immutable.List<Immutable.List<number>>
): number => {
  let endValue: number = 0;
  let previousValue: number = 0;
  const previousDate: number = moment(startDate).subtract(1, 'd').valueOf();
  records.forEach((record: Immutable.List<number>) => {
    if (moment(record.first()).isSame(moment(previousDate), 'd')) {
      previousValue = record.last();
    }
    if (moment(record.first()).isSame(moment(endDate), 'd')) {
      endValue = record.last();
    }
  });

  return endValue - previousValue;
};
